import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FiX, FiSend, FiMessageCircle, FiMinimize2, FiCopy, FiTrash2, FiStar, FiSearch, FiFileText, FiCheckSquare, FiClock, FiPlus } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import './Chatbot.css';

const Chatbot = ({ collectionId, collectionName, collectionAnalysis, isOpen, onClose, onBackToSearch }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hello! I'm your research assistant. I can help you understand and explore the papers in your "${collectionName}" collection. What would you like to know?`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [actionTitle, setActionTitle] = useState('');
  const [actionDescription, setActionDescription] = useState('');
  const [actionPriority, setActionPriority] = useState('medium');
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [chatSessions, setChatSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [includeAnalysis, setIncludeAnalysis] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const suggestedQuestions = [
    "What are the main themes or topics covered in this collection?",
    "Summarize the key findings from the papers in this collection",
    "What methodologies are commonly used in these papers?",
    "Are there any conflicting findings or debates among the papers?",
    "What are the most cited papers in this collection?",
    "What gaps or future research directions are mentioned?",
    "How do these papers relate to each other?",
    "What are the main conclusions across all papers?"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load chat sessions when collection changes
  useEffect(() => {
    if (isOpen && collectionId) {
      fetchChatSessions();
    }
  }, [isOpen, collectionId]);

  const fetchChatSessions = async () => {
    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${collectionId}/chat-sessions`), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const sessions = await response.json();
        setChatSessions(sessions);
      }
    } catch (err) {
      console.error('Failed to fetch chat sessions:', err);
    }
  };

  const saveChatSession = async (messagesToSave) => {
    if (!collectionId || messagesToSave.length <= 1) return; // Don't save if only welcome message
    
    try {
      // If we have an existing session, update it by creating a new one with the same messages
      // (We'll implement proper update endpoint later if needed)
      // For now, we'll create a new session each time, but only if we don't have a current session
      if (currentSessionId) {
        // Session already exists, don't create duplicate
        return;
      }
      
      const response = await fetch(getApiEndpoint(`/api/collections/${collectionId}/chat-sessions`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          title: null, // Auto-generate from first message
          messages: messagesToSave
        })
      });
      
      if (response.ok) {
        const session = await response.json();
        setCurrentSessionId(session.id);
        fetchChatSessions(); // Refresh list
      }
    } catch (err) {
      console.error('Failed to save chat session:', err);
    }
  };

  const loadChatSession = async (sessionId) => {
    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${collectionId}/chat-sessions/${sessionId}`), {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const session = await response.json();
        setMessages(session.messages.map(msg => ({
          role: msg.role,
          content: msg.content
        })));
        setCurrentSessionId(sessionId);
        setShowHistory(false);
        setShowSuggestions(false);
      }
    } catch (err) {
      console.error('Failed to load chat session:', err);
      alert('Failed to load chat session');
    }
  };

  const deleteChatSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this chat session?')) return;
    
    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${collectionId}/chat-sessions/${sessionId}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        fetchChatSessions();
        if (currentSessionId === sessionId) {
          // Reset to new chat if current session was deleted
          setMessages([
            {
              role: 'assistant',
              content: `Hello! I'm your research assistant. I can help you understand and explore the papers in your "${collectionName}" collection. What would you like to know?`
            }
          ]);
          setCurrentSessionId(null);
          setShowSuggestions(true);
        }
      }
    } catch (err) {
      console.error('Failed to delete chat session:', err);
      alert('Failed to delete chat session');
    }
  };

  const startNewChat = () => {
    if (messages.length > 1 && currentSessionId) {
      // Save current session before starting new one
      saveChatSession(messages);
    }
    setMessages([
      {
        role: 'assistant',
        content: `Hello! I'm your research assistant. I can help you understand and explore the papers in your "${collectionName}" collection. What would you like to know?`
      }
    ]);
    setCurrentSessionId(null);
    setShowSuggestions(true);
    setShowHistory(false);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setShowSuggestions(false); // Hide suggestions once user starts chatting
    
    // Add user message
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const requestBody = {
        message: userMessage,
        conversation_history: newMessages.slice(0, -1) // Send all messages except the current user message
      };

      // Include collection analysis if requested and available
      if (includeAnalysis && collectionAnalysis) {
        requestBody.collection_analysis = collectionAnalysis;
      }

      const response = await fetch(getApiEndpoint(`/api/collections/${collectionId}/chat`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to get response');
      }

      const data = await response.json();
      
      // Add assistant response
      const updatedMessages = [...newMessages, { role: 'assistant', content: data.response }];
      setMessages(updatedMessages);
      
      // Save session after each exchange (debounced - only save after assistant responds)
      saveChatSession(updatedMessages);
    } catch (err) {
      setMessages([...newMessages, { 
        role: 'assistant', 
        content: `Sorry, I encountered an error: ${err.message}. Please try again.` 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const handleCopyMessage = (content) => {
    navigator.clipboard.writeText(content);
    // Could add a toast notification here
  };

  const handleClearChat = () => {
    if (window.confirm('Are you sure you want to clear the conversation? The current session will be saved first.')) {
      // Save current session before clearing
      if (messages.length > 1) {
        saveChatSession(messages);
      }
      setMessages([
        {
          role: 'assistant',
          content: `Hello! I'm your research assistant. I can help you understand and explore the papers in your "${collectionName}" collection. What would you like to know?`
        }
      ]);
      setCurrentSessionId(null);
      setShowSuggestions(true); // Show suggestions again after clearing
    }
  };

  const handleSuggestionClick = (question) => {
    setShowSuggestions(false);
    // Add user message directly
    const newMessages = [...messages, { role: 'user', content: question }];
    setMessages(newMessages);
    setLoading(true);

    // Send the question to the API
    fetch(getApiEndpoint(`/api/collections/${collectionId}/chat`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        message: question,
        conversation_history: messages.slice(0, -1) // Send all messages except the welcome message
      })
    })
    .then(response => {
      if (!response.ok) {
        return response.json().then(error => {
          throw new Error(error.detail || 'Failed to get response');
        });
      }
      return response.json();
    })
    .then(data => {
      const updatedMessages = [...newMessages, { role: 'assistant', content: data.response }];
      setMessages(updatedMessages);
      
      // Save session after each exchange
      saveChatSession(updatedMessages);
    })
    .catch(err => {
      setMessages([...newMessages, { 
        role: 'assistant', 
        content: `Sorry, I encountered an error: ${err.message}. Please try again.` 
      }]);
    })
    .finally(() => {
      setLoading(false);
    });
  };

  const handleCreateNote = async () => {
    if (!noteTitle.trim() || !noteContent.trim()) {
      alert('Please provide both a title and content for the note.');
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${collectionId}/notes`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          title: noteTitle.trim(),
          content: noteContent.trim()
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create note');
      }

      alert('Note created successfully!');
      setShowNoteModal(false);
      setNoteTitle('');
      setNoteContent('');
    } catch (err) {
      alert(`Failed to create note: ${err.message}`);
    }
  };

  const handleCloseNoteModal = () => {
    if (noteTitle.trim() || noteContent.trim()) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        setShowNoteModal(false);
        setNoteTitle('');
        setNoteContent('');
      }
    } else {
      setShowNoteModal(false);
      setNoteTitle('');
      setNoteContent('');
    }
  };

  const handleCreateActionItem = async () => {
    if (!actionTitle.trim()) {
      alert('Please provide a title for the action item.');
      return;
    }

    try {
      const response = await fetch(getApiEndpoint(`/api/collections/${collectionId}/actionable-items`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          title: actionTitle.trim(),
          description: actionDescription.trim() || null,
          priority: actionPriority
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create action item');
      }

      alert('Action item created successfully!');
      setShowActionModal(false);
      setActionTitle('');
      setActionDescription('');
      setActionPriority('medium');
    } catch (err) {
      alert(`Failed to create action item: ${err.message}`);
    }
  };

  const handleCloseActionModal = () => {
    if (actionTitle.trim() || actionDescription.trim()) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        setShowActionModal(false);
        setActionTitle('');
        setActionDescription('');
        setActionPriority('medium');
      }
    } else {
      setShowActionModal(false);
      setActionTitle('');
      setActionDescription('');
      setActionPriority('medium');
    }
  };

  // Generate a title from content (first sentence or first 50 chars)
  const generateTitleFromContent = (content) => {
    if (!content) return '';
    // Remove markdown formatting for title generation
    const plainText = content.replace(/[#*_`\[\]()]/g, '').trim();
    const firstSentence = plainText.split(/[.!?]\s/)[0];
    if (firstSentence.length > 0 && firstSentence.length <= 60) {
      return firstSentence;
    }
    return plainText.substring(0, 50).trim() + (plainText.length > 50 ? '...' : '');
  };

  const handleCreateNoteFromMessage = useCallback((messageContent) => {
    const suggestedTitle = generateTitleFromContent(messageContent);
    setNoteTitle(suggestedTitle);
    setNoteContent(messageContent);
    setShowNoteModal(true);
  }, []);

  const handleCreateActionFromMessage = useCallback((messageContent) => {
    const suggestedTitle = generateTitleFromContent(messageContent);
    setActionTitle(suggestedTitle);
    setActionDescription(messageContent);
    setActionPriority('medium');
    setShowActionModal(true);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only handle shortcuts when chatbot is open and no input is focused
      if (!isOpen || document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') {
        return;
      }

      // Ctrl/Cmd + N for new note
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        // Pre-fill with last assistant message if available
        const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant');
        if (lastAssistantMessage) {
          handleCreateNoteFromMessage(lastAssistantMessage.content);
        } else {
          setShowNoteModal(true);
        }
      }

      // Ctrl/Cmd + Shift + A for new action (avoiding Ctrl+A which is "Select All")
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        // Pre-fill with last assistant message if available
        const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant');
        if (lastAssistantMessage) {
          handleCreateActionFromMessage(lastAssistantMessage.content);
        } else {
          setShowActionModal(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, messages, handleCreateNoteFromMessage, handleCreateActionFromMessage]);

  if (!isOpen) return null;

  return (
    <div className={`chatbot-container ph-no-capture ${isOpen ? 'chatbot-in-tab' : ''}`}>
      <div className="chatbot-header">
        <div className="chatbot-header-content">
          <div className="chatbot-icon-wrapper">
            <FiStar className="chatbot-icon-sparkle" />
            <FiMessageCircle className="chatbot-icon" />
          </div>
          <div>
            <h3>Research Assistant</h3>
            <p className="chatbot-subtitle">Ask about papers in "{collectionName}"</p>
          </div>
        </div>
        <div className="chatbot-header-actions">
          <button 
            className="chatbot-action-button" 
            onClick={() => setShowHistory(!showHistory)} 
            title="Chat History"
          >
            <FiClock />
          </button>
          <button 
            className="chatbot-action-button" 
            onClick={startNewChat} 
            title="New Chat"
          >
            <FiPlus />
          </button>
          <button 
            className="chatbot-action-button" 
            onClick={handleClearChat} 
            title="Clear conversation"
          >
            <FiTrash2 />
          </button>
          <button className="chatbot-close" onClick={onClose} title="Close chatbot">
            <FiX />
          </button>
        </div>
      </div>

      {showHistory && (
        <div className="chatbot-history-panel">
          <div className="chatbot-history-header">
            <h4>Chat History</h4>
            <button onClick={() => setShowHistory(false)} className="chatbot-history-close">
              <FiX />
            </button>
          </div>
          <div className="chatbot-history-list">
            {chatSessions.length === 0 ? (
              <div className="chatbot-history-empty">No saved chat sessions yet</div>
            ) : (
              chatSessions.map((session) => (
                <div
                  key={session.id}
                  className={`chatbot-history-item ${currentSessionId === session.id ? 'active' : ''}`}
                  onClick={() => loadChatSession(session.id)}
                >
                  <div className="chatbot-history-item-content">
                    <div className="chatbot-history-item-title">{session.title || 'Untitled Chat'}</div>
                    <div className="chatbot-history-item-meta">
                      {session.message_count} messages • {new Date(session.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    className="chatbot-history-item-delete"
                    onClick={(e) => deleteChatSession(session.id, e)}
                    title="Delete session"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <div className="chatbot-actions-bar">
        {onBackToSearch && (
          <button 
            className="chatbot-action-bar-button" 
            onClick={onBackToSearch}
            title="Back to Search"
          >
            <FiSearch />
            <span>Add Papers</span>
          </button>
        )}
        <button 
          className="chatbot-action-bar-button" 
          onClick={() => {
            // Pre-fill with last assistant message if available
            const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant');
            if (lastAssistantMessage && lastAssistantMessage.content) {
              handleCreateNoteFromMessage(lastAssistantMessage.content);
            } else {
              setShowNoteModal(true);
            }
          }}
          title="Take Notes (Ctrl+N)"
        >
          <FiFileText />
          <span>Take Notes</span>
        </button>
        <button 
          className="chatbot-action-bar-button" 
          onClick={() => {
            // Pre-fill with last assistant message if available
            const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant');
            if (lastAssistantMessage && lastAssistantMessage.content) {
              handleCreateActionFromMessage(lastAssistantMessage.content);
            } else {
              setShowActionModal(true);
            }
          }}
          title="Create Action Item (Ctrl+Shift+A)"
        >
          <FiCheckSquare />
          <span>Create Action</span>
        </button>
      </div>

      <div className="chatbot-messages">
        {messages.map((message, index) => (
          <div key={index} className={`chatbot-message ${message.role}`}>
            {message.role === 'assistant' && (
              <div className="chatbot-avatar assistant-avatar">
                <FiStar />
              </div>
            )}
            <div className="chatbot-message-content-wrapper">
              <div className="chatbot-message-content">
                {message.role === 'assistant' ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {message.content}
                  </ReactMarkdown>
                ) : (
                  message.content
                )}
              </div>
              {message.role === 'assistant' && (
                <div className="chatbot-message-actions" style={{ display: 'flex', gap: '0.25rem', flexDirection: 'column' }}>
                  <button
                    className="chatbot-copy-button"
                    onClick={() => handleCopyMessage(message.content)}
                    title="Copy message"
                  >
                    <FiCopy />
                  </button>
                  <button
                    className="chatbot-quick-note-button"
                    onClick={() => handleCreateNoteFromMessage(message.content)}
                    title="Save as Note"
                    style={{
                      background: 'rgba(139, 92, 246, 0.1)',
                      border: 'none',
                      color: '#8b5cf6',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '6px',
                      opacity: 0,
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                      marginTop: '0.25rem'
                    }}
                  >
                    <FiFileText />
                  </button>
                  <button
                    className="chatbot-quick-action-button"
                    onClick={() => handleCreateActionFromMessage(message.content)}
                    title="Create Action Item"
                    style={{
                      background: 'rgba(245, 158, 11, 0.1)',
                      border: 'none',
                      color: '#f59e0b',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '6px',
                      opacity: 0,
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                      marginTop: '0.25rem'
                    }}
                  >
                    <FiCheckSquare />
                  </button>
                </div>
              )}
            </div>
            {message.role === 'user' && (
              <div className="chatbot-avatar user-avatar">
                <FiMessageCircle />
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="chatbot-message assistant">
            <div className="chatbot-avatar assistant-avatar">
              <FiStar />
            </div>
            <div className="chatbot-message-content-wrapper">
              <div className="chatbot-message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {showSuggestions && messages.length === 1 && (
        <div className="chatbot-suggestions">
          <p className="suggestions-title">Try asking:</p>
          <div className="suggestions-list">
            {suggestedQuestions.map((question, index) => (
              <button
                key={index}
                className="suggestion-button"
                onClick={() => handleSuggestionClick(question)}
                disabled={loading}
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {collectionAnalysis && (
        <div className="analysis-toggle-container">
          <label className="analysis-toggle">
            <input
              type="checkbox"
              checked={includeAnalysis}
              onChange={(e) => setIncludeAnalysis(e.target.checked)}
            />
            <span className="analysis-toggle-label">
              <FiFileText style={{ marginRight: '0.5rem' }} />
              Include Collection Analysis as context
            </span>
          </label>
        </div>
      )}

      <form className="chatbot-input-form" onSubmit={handleSend}>
        <textarea
          ref={inputRef}
          className="chatbot-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask about papers in your collection..."
          rows="1"
          disabled={loading}
        />
        <button 
          type="submit" 
          className="chatbot-send-button"
          disabled={!input.trim() || loading}
          title="Send message"
        >
          <FiSend />
        </button>
      </form>

      {/* Note Modal */}
      {showNoteModal && (
        <div className="modal-overlay chatbot-modal-overlay">
          <div className="modal chatbot-modal" style={{ maxWidth: '600px' }}>
            <button className="modal-close" onClick={handleCloseNoteModal}>
              <FiX />
            </button>
            <h2>Create Note</h2>
            <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
              Save important insights or knowledge from your conversation.
              <br />
              <span style={{ color: '#999', fontSize: '0.85rem' }}>Tip: Use Ctrl+N (Cmd+N on Mac) for quick note creation</span>
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Title
              </label>
              <input
                type="text"
                className="modal-input"
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
                placeholder="Note title..."
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Content
              </label>
              <textarea
                className="modal-textarea"
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Note content..."
                rows="6"
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="modal-button secondary" onClick={handleCloseNoteModal}>
                Cancel
              </button>
              <button className="modal-button primary" onClick={handleCreateNote}>
                Save Note
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Item Modal */}
      {showActionModal && (
        <div className="modal-overlay chatbot-modal-overlay">
          <div className="modal chatbot-modal" style={{ maxWidth: '600px' }}>
            <button className="modal-close" onClick={handleCloseActionModal}>
              <FiX />
            </button>
            <h2>Create Action Item</h2>
            <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
              Create a trackable action item or task from your discussion.
              <br />
              <span style={{ color: '#999', fontSize: '0.85rem' }}>Tip: Use Ctrl+Shift+A (Cmd+Shift+A on Mac) for quick action creation</span>
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Title *
              </label>
              <input
                type="text"
                className="modal-input"
                value={actionTitle}
                onChange={(e) => setActionTitle(e.target.value)}
                placeholder="Action item title..."
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Description
              </label>
              <textarea
                className="modal-textarea"
                value={actionDescription}
                onChange={(e) => setActionDescription(e.target.value)}
                placeholder="Action item description..."
                rows="4"
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Priority
              </label>
              <select
                value={actionPriority}
                onChange={(e) => setActionPriority(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="modal-button secondary" onClick={handleCloseActionModal}>
                Cancel
              </button>
              <button className="modal-button primary" onClick={handleCreateActionItem}>
                Create Action Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;

